import { Controller } from "@hotwired/stimulus"

// Reveals the remote images in one message. MessagesHelper renders them with no `src`, holding
// the sender's URL in `data-blocked-src` instead, so opening a thread fetches nothing and tells
// the sender nothing — this controller is the agent explicitly asking. Client-only and
// deliberately not persisted: the choice applies to this view of this message, and reopening the
// thread blocks again.
export default class extends Controller {
  static targets = ["label", "button", "strip"]

  toggle() {
    this.shown() ? this.hide() : this.show()
  }

  show() {
    this.reveal()
    this.render(true, "Images are shown for this message.", "Hide images")
  }

  // The quoted history arrives in a lazy frame after the card, so its images were never in the
  // scan above. Its frame fires this on load: a blocked image in there raises the strip the card
  // rendered hidden, and if the agent already chose to show images, the new ones follow suit —
  // "Show images" is a decision about this message, not about the part that was on screen then.
  rescan() {
    if (!this.hasButtonTarget) return
    if (this.shown()) {
      this.reveal()
      return
    }
    if (this.hasStripTarget && this.images("[data-blocked-src]").length > 0) this.stripTarget.hidden = false
  }

  reveal() {
    this.images("[data-blocked-src]").forEach((image) => {
      image.addEventListener("error", this.markFailed, { once: true })
      // Kept in a second attribute rather than read back off `src`, because a failed load clears
      // `src` — without this, hiding again would have nothing to restore.
      image.dataset.revealedSrc = image.dataset.blockedSrc
      delete image.dataset.blockedSrc
      delete image.dataset.imageFailed
      image.src = image.dataset.revealedSrc
    })
  }

  // A display toggle, not a retraction: the request has already gone to the sender, which is why
  // the copy never claims the images were un-loaded.
  hide() {
    this.images("[data-revealed-src]").forEach((image) => {
      image.dataset.blockedSrc = image.dataset.revealedSrc
      delete image.dataset.revealedSrc
      delete image.dataset.imageFailed
      image.removeAttribute("src")
    })
    this.render(false, "Images in this message are blocked.", "Show images")
  }

  // Senders take images down, and hosts stop resolving, so a failure here is ordinary rather than
  // exceptional. Dropping the src as well as marking the node is what keeps the browser from
  // painting its broken-image glyph — the placeholder and the sender's alt text render instead.
  markFailed(event) {
    event.target.removeAttribute("src")
    event.target.dataset.imageFailed = "true"
  }

  images(selector) {
    return Array.from(this.element.querySelectorAll(`img${selector}`))
  }

  shown() {
    return this.buttonTarget.getAttribute("aria-expanded") === "true"
  }

  render(shown, label, action) {
    this.buttonTarget.setAttribute("aria-expanded", shown ? "true" : "false")
    this.buttonTarget.textContent = action
    this.labelTarget.textContent = label
  }
}
